/**
 * @file Description pure (sans DOM) de l'icone d'une arme : liste de primitives SVG (viewBox 48x28). Deux armes n'ont jamais la meme description :
 *       silhouette de base, nombre de canons / tubes, lunettes, bipied, tambour, couleur d'accent du comportement, ornements du grade, motif du slot.
 *       Rendue par `weaponIcon` (icons.ts) ; testee dans tests/weapon-icons.test.ts.
 */
import type { GradeDef, GunSilhouette, WeaponDef } from '@config/types';
import type { WeaponBehavior } from '@shared/enums';

export interface Prim {
  tag: 'rect' | 'polygon' | 'circle' | 'line' | 'polyline';
  attrs: Record<string, string | number>;
  /** Contour sombre (forme pleine) ou trait seul. */
  stroke?: boolean;
}

type Box = [number, number, number, number];
interface Shape {
  body: Box;
  barrel: Box;
  grip?: Box;
  stock?: boolean;
  mag?: boolean;
  scope?: boolean;
  fat?: boolean;
}

export const SHAPES: Record<GunSilhouette, Shape> = {
  pistol: { body: [8, 7, 20, 7], barrel: [28, 8, 10, 5], grip: [10, 14, 6, 10] },
  revolver: { body: [8, 6, 20, 9], barrel: [28, 8, 12, 5], grip: [9, 15, 7, 9] },
  smg: { body: [6, 7, 26, 8], barrel: [32, 8.5, 10, 4.5], grip: [10, 15, 6, 9], mag: true },
  shotgun: { body: [8, 8, 18, 6], barrel: [26, 8, 18, 5], stock: true },
  doubleShotgun: { body: [8, 8, 16, 6], barrel: [24, 6.5, 20, 4], stock: true },
  pumpShotgun: { body: [8, 8, 18, 6], barrel: [26, 8, 18, 5], grip: [24, 13, 8, 4], stock: true },
  drumShotgun: { body: [8, 8, 18, 6], barrel: [26, 8, 18, 5], stock: true, mag: true },
  rifle: { body: [8, 8, 22, 6], barrel: [30, 9, 14, 3.6], grip: [12, 14, 5, 8], stock: true },
  boltRifle: { body: [8, 8, 22, 5], barrel: [30, 9, 16, 3], stock: true, scope: true },
  railgun: { body: [6, 7, 24, 8], barrel: [30, 8.5, 16, 5], scope: true, fat: true },
  minigun: { body: [6, 8, 16, 9], barrel: [22, 7, 22, 11], grip: [10, 17, 6, 7] },
  gatling: { body: [6, 8, 14, 9], barrel: [20, 6.5, 24, 12], grip: [9, 17, 6, 7] },
  cannon: { body: [6, 7, 20, 11], barrel: [26, 6, 18, 13], fat: true },
  grenadeLauncher: { body: [8, 8, 18, 8], barrel: [26, 7, 14, 10], grip: [11, 16, 6, 8], fat: true },
  sniper: { body: [6, 8, 24, 5], barrel: [30, 9, 16, 3], stock: true, scope: true },
  launcher: { body: [4, 8, 38, 11], barrel: [38, 6, 6, 15], fat: true },
  energyCannon: { body: [6, 6, 22, 13], barrel: [28, 8, 16, 9], fat: true, scope: true },
};

/** Couleur d'accent du comportement (bande sur le corps + lueur de bouche). */
export const BEHAVIOR_ACCENT: Record<WeaponBehavior, string> = {
  single: '#e9eef5',
  pierce: '#27d3ff',
  shotgun: '#ff9a3c',
  explosive: '#ff4d4d',
  laser: '#ff3df0',
  missile: '#7dff5a',
  blastSniper: '#b26bff',
};

const DARK = '#2b2017';
const round = (n: number): number => Math.round(n * 100) / 100;

/**
 * Primitives de l'icone. Ornements cumulatifs selon l'index du grade (0..12) :
 * anneaux de canon (>=3), rail supérieur (>=4), aileron (>=6), noyau lumineux (>=8), couronne de 1 a 3 pointes (>=10).
 * Le slot (0..4) ajoute un motif sur le corps : aucun, rayures, points, chevrons, double bande.
 */
export const weaponIconSpec = (def: WeaponDef, grade: GradeDef): Prim[] => {
  const v = def.visual;
  const sh = SHAPES[v.silhouette];
  const t = grade.index;
  const P = grade.colors.primary;
  const S = grade.colors.secondary;
  const accent = BEHAVIOR_ACCENT[def.behavior];
  const n = Math.max(1, v.barrels);
  const out: Prim[] = [];
  const box = (b: Box, fill: string, rx = 1.5): void => {
    out.push({ tag: 'rect', stroke: true, attrs: { x: round(b[0]), y: round(b[1]), width: round(b[2]), height: round(b[3]), rx, fill } });
  };
  const line = (x1: number, y1: number, x2: number, y2: number, color = DARK, w = 1, opacity = 0.7): void => {
    out.push({ tag: 'line', attrs: { x1: round(x1), y1: round(y1), x2: round(x2), y2: round(y2), stroke: color, 'stroke-width': w, 'stroke-linecap': 'round', opacity } });
  };

  const body = sh.body;
  const barrel: Box = [...sh.barrel];
  const launcher = v.silhouette === 'launcher';
  const clustered = v.silhouette === 'gatling' || v.silhouette === 'minigun' || launcher;
  // plusieurs canons paralleles (fusils X / XXX...) : le canon s'epaissit
  if (n > 1 && !clustered) {
    const extra = Math.min(8, (n - 1) * 1.6);
    barrel[1] -= extra / 2;
    barrel[3] += extra;
  }

  // ---- arriere : crosse, poignee, chargeur, tambour
  if (sh.stock) {
    out.push({ tag: 'polygon', stroke: true, attrs: { points: `${body[0] + 1},${body[1] + 1} 2,${body[1] + 6} 2,${body[1] + 11} ${body[0] + 3},${body[1] + 6}`, fill: S } });
  }
  if (sh.grip) box(sh.grip, S, 2);
  if (sh.mag) box([body[0] + 10, body[1] + body[3] - 1, 6, 9], S, 2);
  if (v.drumMag && !sh.mag) out.push({ tag: 'circle', stroke: true, attrs: { cx: body[0] + 9, cy: body[1] + body[3] + 3, r: 5, fill: S } });
  // ---- bipied
  if (v.bipod) {
    out.push({ tag: 'polyline', attrs: { points: `${barrel[0] + 3},${barrel[1] + barrel[3]} ${barrel[0] + 1},26 ${barrel[0] + 8},${barrel[1] + barrel[3]} ${barrel[0] + 11},26`, fill: 'none', stroke: DARK, 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' } });
  }
  // ---- aileron (haut de gamme) sous le canon
  if (t >= 6) {
    const fx = barrel[0] + 2;
    const fy = barrel[1] + barrel[3];
    out.push({ tag: 'polygon', stroke: true, attrs: { points: `${fx},${fy} ${fx + 8},${fy} ${fx + 3},${fy + 5}`, fill: accent } });
  }
  // ---- canon + corps
  box(barrel, S, sh.fat ? 3 : 1.2);
  box(body, P, 2.5);

  // ---- canons / tubes multiples : cloisons
  if (n > 1) {
    const area: Box = launcher ? body : barrel;
    const rows = launcher ? Math.min(n, 5) : Math.min(n, 8);
    for (let i = 1; i < rows; i++) line(area[0] + 1.5, area[1] + (area[3] * i) / rows, area[0] + area[2] - 1.5, area[1] + (area[3] * i) / rows);
    if (launcher && n > 5) {
      const cols = Math.ceil(n / 5);
      for (let i = 1; i < cols; i++) line(area[0] + (area[2] * i) / cols, area[1] + 1.5, area[0] + (area[2] * i) / cols, area[1] + area[3] - 1.5);
    }
  }
  // ---- anneaux de canon (grade >= 3)
  const rings = Math.min(3, Math.floor(t / 3));
  for (let i = 0; i < rings; i++) {
    const rx = barrel[0] + 3 + i * 4;
    if (rx < barrel[0] + barrel[2] - 3) line(rx, barrel[1] + 0.5, rx, barrel[1] + barrel[3] - 0.5, DARK, 1.2, 0.85);
  }
  // ---- bande d'accent du comportement + motif du slot
  out.push({ tag: 'rect', attrs: { x: round(body[0] + 2), y: round(body[1] + body[3] / 2 - 0.8), width: round(Math.max(6, body[2] - 6)), height: 1.6, rx: 0.8, fill: accent, opacity: 0.95 } });
  const bx = body[0];
  const by = body[1];
  const bw = body[2];
  const bh = body[3];
  switch (def.slot) {
    case 1:
      for (let i = 0; i < 3; i++) line(bx + bw * (0.3 + i * 0.2), by + 1, bx + bw * (0.2 + i * 0.2), by + bh - 1, DARK, 1, 0.45);
      break;
    case 2:
      for (let i = 0; i < 3; i++) out.push({ tag: 'circle', attrs: { cx: round(bx + bw * (0.34 + i * 0.16)), cy: round(by + bh * 0.28), r: 0.9, fill: DARK, opacity: 0.55 } });
      break;
    case 3:
      for (let i = 0; i < 2; i++) out.push({ tag: 'polyline', attrs: { points: `${round(bx + bw * (0.36 + i * 0.18))},${by + 1.2} ${round(bx + bw * (0.42 + i * 0.18))},${round(by + bh * 0.3)} ${round(bx + bw * (0.36 + i * 0.18))},${round(by + bh * 0.42)}`, fill: 'none', stroke: DARK, 'stroke-width': 1, opacity: 0.5, 'stroke-linejoin': 'round' } });
      break;
    case 4:
      line(bx + 3, by + bh - 1.6, bx + bw - 4, by + bh - 1.6, DARK, 1, 0.5);
      line(bx + 3, by + 1.6, bx + bw - 4, by + 1.6, DARK, 1, 0.5);
      break;
    default:
      break;
  }
  // ---- rail superieur (grade >= 4)
  if (t >= 4) {
    for (let i = 0; i < 3; i++) out.push({ tag: 'rect', attrs: { x: round(bx + 3 + i * 4), y: round(by - 1.6), width: 2.6, height: 1.8, rx: 0.5, fill: DARK, opacity: 0.85 } });
  }
  // ---- lunettes
  if (v.scope > 0 || sh.scope) {
    box([body[0] + 6, body[1] - 5, 11, 3.5], '#4a5568', 1.5);
    if (v.scope === 2) box([body[0] + 12, body[1] - 8.5, 8, 3.2], '#37404f', 1.5);
    out.push({ tag: 'circle', attrs: { cx: round(body[0] + 16.2), cy: round(body[1] - 3.2), r: 1, fill: accent } });
  }
  // ---- noyau lumineux (grade >= 8)
  if (t >= 8) {
    out.push({ tag: 'circle', stroke: true, attrs: { cx: round(bx + bw * 0.62), cy: round(by + bh / 2), r: 2.6, fill: '#ffffff' } });
    out.push({ tag: 'circle', attrs: { cx: round(bx + bw * 0.62), cy: round(by + bh / 2), r: 1.2, fill: accent } });
  }
  // ---- couronne (grade >= 10) : 1 a 3 pointes
  for (let i = 0; i < Math.max(0, Math.min(3, t - 9)); i++) {
    const cx = bx + bw - 4 - i * 4.5;
    out.push({ tag: 'polygon', stroke: true, attrs: { points: `${round(cx - 2)},${by} ${round(cx)},${by - 4.5} ${round(cx + 2)},${by}`, fill: S } });
  }
  // ---- lueur de bouche
  out.push({ tag: 'circle', stroke: true, attrs: { cx: round(barrel[0] + barrel[2] - 1.2), cy: round(barrel[1] + barrel[3] / 2), r: 1.9, fill: accent } });
  return out;
};

/** Echelle de l'icone : les grandes armes occupent toute la vignette, les petites un peu moins (autour du centre 24,14). */
export const weaponIconScale = (def: WeaponDef): number => round(0.84 + Math.min(1, Math.max(0, (def.visual.size - 1) / 2)) * 0.16);
